import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useTheme } from "../ThemeProvider";
import api from "../../services/api";
import { buildWhatsAppQuickReplies, normalizeWaIdioma } from "../../utils/whatsappQuickReplies";
import { buildGestionVentasQuickReplies, nombreDocumentoLegible } from "../../utils/gestionVentasQuickReplies";

function primerNombre(n) {
  const t = String(n ?? "").trim();
  return t ? t.split(/\s+/)[0] : "";
}

function aplicarPlaceholders(texto, nombre, agente) {
  return String(texto ?? "")
    .replace(/\{nombre\}/g, nombre || "")
    .replace(/\{agente\}/g, agente || "")
    .replace(/VitalCasa/gi, "Vitalcasa");
}

export default function ModalWhatsAppMulti({ isOpen, onClose, contactos = [], crmContext = null, documentosNas = null, quickRepliesExpediente = null }) {
  const { theme } = useTheme();
  const c = theme.colors;
  const isDark = theme.name === "dark";
  const tx = isDark ? "#ffffff" : c.textPrimary;

  const [mensaje, setMensaje] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const [progreso, setProgreso] = useState("");
  const [agenteNombre, setAgenteNombre] = useState("");
  const [quickReplies, setQuickReplies] = useState([]);

  const destinatarios = contactos.filter((x) => x.telefono);

  useEffect(() => {
    if (!isOpen) return;
    setMensaje("");
    setError("");
    setProgreso("");
    let cancelled = false;

    const cargarAgenteYReplies = async () => {
      let nombreCompleto = "";
      let sexoEmisor = "";
      try {
        const r = await api.get("/auth/me");
        const u = r.data?.usuario || r.data?.user || r.data;
        nombreCompleto = String(u?.nombre || u?.name || r.data?.nombre || "").trim();
        sexoEmisor = u?.sexo || r.data?.sexo || "";
        setAgenteNombre(primerNombre(nombreCompleto));
      } catch {
        setAgenteNombre("");
      }

      if (cancelled) return;

      if (quickRepliesExpediente) {
        const archivos = documentosNas?.archivos?.length
          ? documentosNas.archivos
          : (quickRepliesExpediente.archivos || []);
        setQuickReplies(buildGestionVentasQuickReplies({
          nombreUsuario: nombreCompleto,
          sexo: sexoEmisor,
          poblacion: quickRepliesExpediente.poblacion,
          direccion: quickRepliesExpediente.direccion,
          archivos,
        }));
        return;
      }

      const idiomaWa = normalizeWaIdioma("ES");
      try {
        const r = await api.get("/whatsapp-plantillas/runtime", { params: { count: 0, idioma: idiomaWa } });
        if (cancelled) return;
        if (r.data?.ok && Array.isArray(r.data.replies)) {
          setQuickReplies(r.data.replies);
        } else {
          setQuickReplies(buildWhatsAppQuickReplies(0, idiomaWa));
        }
      } catch {
        if (!cancelled) setQuickReplies(buildWhatsAppQuickReplies(0, idiomaWa));
      }
    };

    cargarAgenteYReplies();
    return () => { cancelled = true; };
  }, [isOpen, quickRepliesExpediente, documentosNas]);

  if (!isOpen) return null;

  const inp = {
    padding: "8px 10px", borderRadius: 6, fontSize: 12, width: "100%",
    background: isDark ? "#1a2030" : c.inputBg,
    border: `1px solid ${isDark ? "#5a6a80" : c.inputBorder}`,
    color: tx, outline: "none", fontFamily: "inherit", boxSizing: "border-box",
    resize: "vertical", minHeight: 100,
  };

  const enviar = async () => {
    const msg = mensaje.trim();
    if (!msg) { setError("Escribe un mensaje."); return; }
    if (!destinatarios.length) { setError("Ningún destinatario tiene teléfono."); return; }

    setEnviando(true);
    setError("");
    setProgreso("");
    const token = localStorage.getItem("token");
    const crmPayload = {};
    if (crmContext?.cliente_id) crmPayload.cliente_id = crmContext.cliente_id;
    if (crmContext?.lead_id) crmPayload.lead_id = crmContext.lead_id;

    const docs = documentosNas?.archivos || [];
    const conDocs = Boolean(documentosNas?.expedienteId && docs.length);

    let ok = 0;
    let fail = 0;
    let ultimoError = "";

    try {
      for (let i = 0; i < destinatarios.length; i++) {
        const ct = destinatarios[i];
        const phone = String(ct.telefono).replace(/\D/g, "");
        const nombre = ct.nombre || "cliente";
        const texto = aplicarPlaceholders(msg, nombre, agenteNombre).slice(0, 1000);

        if (conDocs) {
          setProgreso(`Enviando a ${nombre} (mensaje + ${docs.length} documento(s))…`);
          try {
            const adj = await api.post("/whatsapp/enviar-documentos-nas", {
              telefono: phone,
              mensaje: texto,
              expediente_id: documentosNas.expedienteId,
              archivos: docs,
              cliente_id: ct.cliente_id || crmContext?.cliente_id || null,
              lead_id: ct.lead_id || crmContext?.lead_id || null,
            }, { timeout: 120000 });
            const n = Number(adj.data?.enviados) || 0;
            if (adj.data?.ok && n > 0) ok += 1;
            else {
              fail += 1;
              ultimoError = adj.data?.error || "No se pudo adjuntar el documento";
            }
          } catch (e) {
            fail += 1;
            ultimoError = e.code === "ECONNABORTED"
              ? "Tiempo de espera agotado al enviar el documento. Comprueba WhatsApp conectado."
              : (e.response?.data?.error || e.message || "Error al enviar");
          }
        } else {
          setProgreso(`Enviando ${i + 1}/${destinatarios.length}: ${nombre}`);
          try {
            const resp = await fetch("/api/whatsapp/enviar", {
              method: "POST",
              headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
              body: JSON.stringify({ telefono: phone, mensaje: texto, ...crmPayload }),
              signal: AbortSignal.timeout(120000),
            });
            const data = await resp.json().catch(() => ({}));
            if (resp.ok && data.ok !== false) ok += 1;
            else {
              fail += 1;
              ultimoError = data.error || "Error al enviar";
            }
          } catch (e) {
            fail += 1;
            ultimoError = e.name === "TimeoutError"
              ? "Tiempo de espera agotado. Comprueba WhatsApp conectado en Configuración."
              : (e.message || "Error al enviar");
          }
        }

        if (i < destinatarios.length - 1) await new Promise((r) => setTimeout(r, 600));
      }
    } finally {
      setEnviando(false);
      setProgreso("");
    }

    if (fail === 0) {
      onClose?.();
    } else if (ok === 0) {
      setError(ultimoError || "No se pudo enviar el WhatsApp.");
    } else {
      setError(`Enviados: ${ok}. Fallidos: ${fail}.${ultimoError ? ` ${ultimoError}` : ""}`);
    }
  };

  return createPortal(
    <div style={{
      position: "fixed", inset: 0, zIndex: 12000, background: "rgba(0,0,0,0.5)",
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit",
    }} onMouseDown={(e) => { if (e.target === e.currentTarget && !enviando) onClose?.(); }}>
      <div style={{
        width: 440, maxWidth: "94vw", background: isDark ? "#1e2330" : "#fff",
        borderRadius: 10, padding: 16, border: `1px solid ${isDark ? "#4a5568" : c.border}`,
        color: tx,
      }}>
        <div style={{ fontSize: 15, fontWeight: 800, marginBottom: 8 }}>WhatsApp a varios</div>
        <div style={{ fontSize: 11, marginBottom: 10, opacity: 0.85 }}>
          {destinatarios.length} destinatario(s):{" "}
          {destinatarios.map((d) => d.nombre).join(", ") || "—"}
        </div>
        {documentosNas?.archivos?.length > 0 && (
          <div style={{
            fontSize: 11, marginBottom: 10, padding: "6px 10px", borderRadius: 6,
            background: isDark ? "rgba(56,189,248,0.12)" : "#e0f2fe",
            color: isDark ? "#7dd3fc" : "#0369a1",
          }}>
            Se adjuntarán {documentosNas.archivos.length} documento(s) del expediente: {documentosNas.archivos.map(nombreDocumentoLegible).join(", ")}
          </div>
        )}
        <textarea
          style={inp}
          value={mensaje}
          onChange={(e) => setMensaje(e.target.value)}
          placeholder="Mensaje (usa {nombre} para personalizar)"
          disabled={enviando}
        />
        {quickReplies.length > 0 && (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8 }}>
            {quickReplies.map((qr) => (
              <button
                key={qr.id || qr.label}
                type="button"
                disabled={enviando}
                onClick={() => {
                  const texto = quickRepliesExpediente
                    ? qr.text
                    : aplicarPlaceholders(qr.text, destinatarios[0]?.nombre, agenteNombre);
                  setMensaje(texto.slice(0, 1000));
                }}
                style={{
                  fontSize: 10, padding: "3px 8px", borderRadius: 12, cursor: "pointer",
                  border: `1px solid ${isDark ? "#4ade80" : "#16a34a"}`,
                  background: isDark ? "rgba(74,222,128,0.1)" : "rgba(22,163,74,0.08)",
                  color: isDark ? "#4ade80" : "#15803d",
                }}
              >
                {qr.emoji} {qr.label}
              </button>
            ))}
          </div>
        )}
        {progreso && <div style={{ fontSize: 11, marginTop: 8, color: c.accentBlue }}>{progreso}</div>}
        {error && <div style={{ fontSize: 11, marginTop: 8, color: c.accentRed }}>{error}</div>}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
          <button type="button" disabled={enviando} onClick={onClose}
            style={{ padding: "6px 14px", borderRadius: 5, border: "none", cursor: "pointer", background: isDark ? "#374151" : "#e5e7eb", color: tx }}>
            Cancelar
          </button>
          <button type="button" disabled={enviando} onClick={enviar}
            style={{ padding: "6px 14px", borderRadius: 5, border: "none", cursor: "pointer", background: "#16a34a", color: "#fff", fontWeight: 700 }}>
            {enviando ? "Enviando…" : `Enviar (${destinatarios.length})`}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
